import { Separator } from '@avanta/shared/components/ui/Separator';
import { SPECIALITY_ICONS, AppointmentDurationLabels } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { useGetSpecialityOptions } from '@avanta/shared/hooks/useGetSpecialityOptions';
import { cn } from '@avanta/shared/utils/cn';
import { timezoneOptions } from '@avanta/shared/utils/date';
import { Building, Globe, MapPin, Mic, User } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  AppointmentsReservePayload,
  CONTACT_METHOD_ICONS,
} from 'pages/Appointments/AppointmentsReserve/types';
import { useUserStore } from 'stores/user';
import { AppointmentType } from 'types/appointments';
import { PsychologistLanguages, Rating } from '../PsychoCard/components';
import { StepSection } from '../StepSection';
import { SearchSummaryItem } from './SearchSummaryItem';

const PSYCHOLOGISTS_KEY_PREFIX = 'AppointmentsReserve.Steps.psychologist.';

interface SearchSummaryProps {
  title?: string;
  className?: string;
  showDetails?: boolean;
}

export const SearchSummary = ({
  title,
  className,
  showDetails = false,
}: React.PropsWithChildren<SearchSummaryProps>): React.ReactElement => {
  const { watch } = useFormContext();
  const [
    psychologist,
    specialityBooked,
    durationType,
    contactMethod,
    city,
    backofficeCenterId,
    timezone,
  ] = watch([
    AppointmentsReservePayload.psychologist,
    AppointmentsReservePayload.specialityBooked,
    AppointmentsReservePayload.durationType,
    AppointmentsReservePayload.contactMethod,
    AppointmentsReservePayload.city,
    AppointmentsReservePayload.backofficeCenterId,
    AppointmentsReservePayload.timezone,
  ]);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const patientId = useUserStore(state => state.user?.id);
  const { data: specialities } = useFetchSpecialities(patientId ?? '');
  const centersOptions = useGetSpecialityOptions(AppointmentsReservePayload.backofficeCenterId);

  const speciality = specialities?.find(speciality => speciality.id === specialityBooked);
  const durationLabel =
    AppointmentDurationLabels[durationType as keyof typeof AppointmentDurationLabels];
  const isRemoteAppointment = contactMethod === AppointmentType.Video;
  const center = centersOptions?.find(
    center => String(center.value) === String(backofficeCenterId)
  );

  return (
    <StepSection title={title} containerClassName="am-lg:gap-2">
      <div
        className={cn(
          'flex flex-col gap-1 am-lg:gap-2 am-lg:p-6 p-2 border border-teal-300 rounded-lg am-lg:w-68 w-full relative',
          className
        )}
      >
        <div className="flex flex-col am-lg:gap-4 gap-0">
          <div className="absolute top-1 am-lg:top-4 right-2 am-lg:right-4">
            <Rating
              rating={psychologist?.averageRate}
              numberOfRatings={psychologist?.numberOfRatings}
            />
          </div>
          <div className="flex am-lg:flex-col gap-2 am-lg:gap-4 items-center am-lg:items-start">
            <img className="size-14" src={psychologist?.imageCircular} alt={psychologist?.name} />
            <div className="flex flex-col">
              <p className="flex justify-start text-denim-800 am-lg:text-base text-sm font-medium leading-5">
                {psychologist?.name} {psychologist?.surname}
              </p>
              {psychologist?.professionalNumber && (
                <p className="text-xs font-medium text-gray-400 leading-4">
                  {t('AppointmentsReserve.PsychoCard.professionalNumber', {
                    professionalNumber: psychologist.professionalNumber,
                  })}
                </p>
              )}
            </div>
          </div>
          <PsychologistLanguages
            spokenLanguages={psychologist?.spokenLanguages}
            className="ml-16 am-lg:ml-0"
          />
        </div>
        {showDetails && (
          <>
            <Separator className="bg-teal-300 my-3" />
            <div className="grid grid-cols-2 gap-x-7 gap-y-2 am-lg:grid-cols-1 items-start">
              <div className="flex flex-col gap-2 order-3 am-lg:order-0">
                <SearchSummaryItem
                  icon={SPECIALITY_ICONS[specialityBooked as keyof typeof SPECIALITY_ICONS] ?? User}
                  label={t(
                    `AppointmentsReserve.Steps.speciality.options.${speciality?.name ?? specialityBooked}`
                  )}
                />
                <SearchSummaryItem
                  icon={
                    CONTACT_METHOD_ICONS[contactMethod as keyof typeof CONTACT_METHOD_ICONS] ?? Mic
                  }
                  label={`${t(`${PSYCHOLOGISTS_KEY_PREFIX}yourSelection.contactMethod.${contactMethod}`)} (${durationLabel})`}
                />
              </div>
              <div className="flex flex-col gap-2 order-3 am-lg:order-0">
                {isRemoteAppointment && (
                  <SearchSummaryItem
                    icon={Globe}
                    label={timezoneOptions.find(option => option.value === timezone)?.label ?? ''}
                  />
                )}
                {city && <SearchSummaryItem icon={MapPin} label={city} />}
                {center && (
                  <SearchSummaryItem
                    icon={Building}
                    label={center.label}
                    description={center.description}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </StepSection>
  );
};
